// ============================================================================
// COUNTRY DATASOURCE - Guide for adding new countries
// ============================================================================

import type { Currencies } from '../_kernel/Currency'
import type { Languages } from '@s4l/i18n/types/Languages'
import type { MeasurementSystems } from '../shared/domain/MeasurementSystem'
import type { Markets } from '../shared/domain/Market'
import type { CountryConfigKey } from '../shared/domain/CountryConfig'
import type { Countries } from '../_kernel/Country'

// ============================================================================
// TYPES
// ============================================================================

export type FeedbackMessageType = 'email' | 'text'
export type CalendarMonthFormat = 'short' | 'long'
export type CalendarWeekStartsOn = 0 | 1 | 6
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type CountryConfig = {
  countryCode: Countries
  countryLanguage: string
  countryName: string
  contactEmail: string
  currency: Currencies
  currencySymbol: string
  language: Languages
  locale: string
  market: Markets
  phone: string | null
  measurementSystem: MeasurementSystems
  subdomain: string | null
  topdomain?: string | null
  helpCenterUrl: string
  faqsUrl: string
  chatbot: boolean
  feedbackMessageType: FeedbackMessageType
  autoNewsletterSubscribe: boolean
  overrideCreditCardLogos?: string[] | null
  priceDropProtection: boolean
  paymentModal?: boolean
  isAsync?: boolean
  calendar: {
    weekStartsOn: CalendarWeekStartsOn
    monthFormat: CalendarMonthFormat
    daysOfBooking: number
    defaultBookingDays: number
    inanitionDays: number
  }
}

export type CountryDataSource = Record<CountryConfigKey, CountryConfig>

// ============================================================================
// CONSTANTS
// ============================================================================

// prettier-ignore
export const COUNTRY_CONFIG_KEYS = ['gb', 'fr', 'us', 'de', 'it', 'es', 'nl', 'beFR', 'beNL', 'caEN', 'caFR', 'dk', 'gr', 'ie', 'no', 'at', 'pl', 'pt', 'ro', 'chDE', 'chFR', 'chIT', 'sk', 'za', 'fi', 'se', 'tr', 'au', 'mx', 'br', 'ee', 'hr', 'lt', 'lv', 'sl', 'nz', 'hu', 'cz', 'xgAR', 'xgEN', 'aeAR', 'aeEN', 'egAR', 'egEN', 'sg', 'ec', 'dz', 'zz', 'co'] as const

// prettier-ignore
export const MARKETS = ['ae', 'at', 'au', 'be', 'br', 'ca', 'ch', 'co', 'cz', 'de', 'dk', 'dz', 'ec', 'ee', 'eg', 'es', 'fi', 'fr', 'gr', 'hr', 'hu', 'ie', 'it', 'lt', 'lv', 'mx', 'nl', 'no', 'nz', 'pl', 'pt', 'ro', 'se', 'sg', 'si', 'sk', 'tr', 'uk', 'us', 'xg', 'za', 'zz'] as const

// prettier-ignore
export const TEST_ONLY_MARKETS = ['co'] as const

export const ALLOWED_MARKETS_FOR_TESTS = MARKETS.filter(
  (market): market is Exclude<(typeof MARKETS)[number], (typeof TEST_ONLY_MARKETS)[number]> =>
    !TEST_ONLY_MARKETS.includes(market as (typeof TEST_ONLY_MARKETS)[number])
)

// prettier-ignore
export const COUNTRIES = ['aa', 'ad', 'ae', 'af', 'ag', 'ai', 'al', 'am', 'an', 'ao', 'ar', 'as', 'at', 'au', 'aw', 'ax', 'az', 'ba', 'bb', 'bd', 'be', 'bf', 'bg', 'bh', 'bi', 'bj', 'bk', 'bl', 'bm', 'bn', 'bo', 'bq', 'br', 'bs', 'bt', 'bv', 'bw', 'by', 'bz', 'ca', 'cc', 'cd', 'cd', 'cf', 'cg', 'ch', 'ci', 'ck', 'cl', 'cm', 'cn', 'co', 'cr', 'cu', 'cv', 'cw', 'cx', 'cy', 'cz', 'de', 'dj', 'dk', 'dm', 'do', 'dz', 'ec', 'ee', 'eg', 'eh', 'er', 'es', 'et', 'fi', 'fj', 'fk', 'fm', 'fo', 'fr', 'ga', 'gb', 'gd', 'ge', 'gf', 'gg', 'gh', 'gi', 'gl', 'gm', 'gn', 'gp', 'gq', 'gr', 'gs', 'gt', 'gu', 'gw', 'gy', 'hk', 'hm', 'hn', 'hr', 'ht', 'hu', 'id', 'ie', 'il', 'im', 'in', 'io', 'iq', 'ir', 'is', 'it', 'je', 'jm', 'jo', 'jp', 'ke', 'kg', 'kh', 'ki', 'km', 'kn', 'kr', 'kv', 'kw', 'ky', 'kz', 'la', 'lb', 'lc', 'li', 'lk', 'lr', 'ls', 'lt', 'lu', 'lv', 'ly', 'ma', 'mc', 'md', 'me', 'mf', 'mg', 'mh', 'mk', 'ml', 'mm', 'mn', 'mo', 'mp', 'mq', 'mr', 'ms', 'mt', 'mu', 'mv', 'mw', 'mx', 'my', 'mz', 'na', 'nc', 'ne', 'nf', 'ng', 'ni', 'nl', 'no', 'np', 'nr', 'nu', 'ny', 'nz', 'om', 'pa', 'pe', 'pf', 'pg', 'ph', 'pk', 'pl', 'pm', 'pn', 'pr', 'ps', 'pt', 'pw', 'py', 'qa', 're', 'ro', 'rs', 'ru', 'rw', 'sa', 'sb', 'sc', 'sd', 'se', 'sf', 'sg', 'sh', 'si', 'sj', 'sk', 'sl', 'sm', 'sn', 'so', 'sr', 'ss', 'st', 'sv', 'sx', 'sy', 'sz', 'tc', 'td', 'tf', 'tg', 'th', 'tj', 'tk', 'tl', 'tm', 'tn', 'to', 'tr', 'tt', 'tv', 'tw', 'tz', 'ua', 'ug', 'uk', 'um', 'us', 'uy', 'uz', 'va', 'vc', 've', 'vg', 'vi', 'vn', 'vu', 'wf', 'ws', 'xg', 'xk', 'xo', 'ye', 'yt', 'za', 'zm', 'zw', 'zz'] as const

// prettier-ignore
export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'DKK', 'NOK', 'PLN', 'RON', 'CHF', 'ZAR', 'SEK', 'TRY', 'AUD', 'MXN', 'BRL', 'NZD', 'HUF', 'CZK', 'SAR', 'AED', 'DZD', 'SGD'] as const

type AncillaryLanguageConfig = { carRental: string; transfer: string }

export const MARKET_TO_ANCILLARY_LANGUAGE: Record<Markets | 'gb', AncillaryLanguageConfig> = {
  ae: { carRental: 'en-us', transfer: 'en' },
  at: { carRental: 'de-de', transfer: 'de' },
  au: { carRental: 'en-us', transfer: 'en' },
  be: { carRental: 'fr-fr', transfer: 'fr' },
  br: { carRental: 'pt-br', transfer: 'pt' },
  ca: { carRental: 'en-us', transfer: 'en' },
  ch: { carRental: 'de-de', transfer: 'de' },
  co: { carRental: 'es-es', transfer: 'es' },
  cz: { carRental: 'en-us', transfer: 'en' },
  de: { carRental: 'de-de', transfer: 'de' },
  dk: { carRental: 'da-dk', transfer: 'sv' },
  dz: { carRental: 'fr-fr', transfer: 'fr' },
  ec: { carRental: 'es-es', transfer: 'es' },
  ee: { carRental: 'en-us', transfer: 'en' },
  eg: { carRental: 'en-us', transfer: 'en' },
  es: { carRental: 'es-es', transfer: 'es' },
  fi: { carRental: 'en-us', transfer: 'sv' },
  fr: { carRental: 'fr-fr', transfer: 'fr' },
  gb: { carRental: 'en-gb', transfer: 'en' },
  gr: { carRental: 'en-us', transfer: 'en' },
  hr: { carRental: 'en-us', transfer: 'en' },
  hu: { carRental: 'en-us', transfer: 'en' },
  ie: { carRental: 'en-gb', transfer: 'en' },
  it: { carRental: 'it-it', transfer: 'it' },
  lt: { carRental: 'en-us', transfer: 'en' },
  lv: { carRental: 'en-us', transfer: 'en' },
  mx: { carRental: 'es-es', transfer: 'es' },
  nl: { carRental: 'nl-nl', transfer: 'nl' },
  no: { carRental: 'nb-no', transfer: 'sv' },
  pl: { carRental: 'en-us', transfer: 'en' },
  pt: { carRental: 'pt-pt', transfer: 'pt' },
  ro: { carRental: 'en-us', transfer: 'en' },
  se: { carRental: 'sv-se', transfer: 'sv' },
  sg: { carRental: 'en-us', transfer: 'en' },
  si: { carRental: 'en-us', transfer: 'en' },
  sk: { carRental: 'en-us', transfer: 'en' },
  tr: { carRental: 'en-us', transfer: 'en' },
  uk: { carRental: 'en-gb', transfer: 'en' },
  us: { carRental: 'en-us', transfer: 'en' },
  xg: { carRental: 'en-us', transfer: 'en' },
  za: { carRental: 'en-us', transfer: 'en' },
  nz: { carRental: 'en-us', transfer: 'en' },
  zz: { carRental: 'en-us', transfer: 'en' },
}

const HELPCENTER_URL = 'https://help.stayforlong.com'

const HELPCENTER_PATH: Record<string, string> = {
  en: '/en',
  fr: '/fr',
  de: '/de',
  it: '/it',
  es: '/es',
  nl: '/nl',
  pl: '/pl',
  pt: '/pt',
  sk: '/sk',
  ar: '/ar',
  'pt-br': '/pt-br',
}

const FAQS_PATH: Record<string, string> = {
  en: '/en/categories/7-faq-s',
  fr: '/fr/categories/30-questions-frequemment-posees',
  de: '/de/categories/33-haeufig-gestellte-fragen',
  it: '/it/categories/39-domande-frequenti',
  es: '/es/categories/26-preguntas-frecuentes',
  nl: '/nl/categories/56-vaak-gestelde-vragen',
  pl: '/pl/categories/62-najcz-ciej-zadawane-pytania',
  pt: '/pt/categories/50-perguntas-mais-frequentes',
  sk: '/sk/categories/70-platby-a-nahrady',
  ar: '/ar/categories/156',
  'pt-br': '/pt-br/categories/78-perguntas-frequentes',
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_CALENDAR = {
  weekStartsOn: 1 as CalendarWeekStartsOn,
  monthFormat: 'short' as CalendarMonthFormat,
  daysOfBooking: 1,
  defaultBookingDays: 3,
  inanitionDays: 0,
}

const DEFAULT_COMMON_PROPS = {
  chatbot: false,
  feedbackMessageType: 'text' as FeedbackMessageType,
  autoNewsletterSubscribe: false,
  overrideCreditCardLogos: null,
  priceDropProtection: true,
  paymentModal: true,
  isAsync: true,
  measurementSystem: 'metric' as MeasurementSystems,
}

// ============================================================================
// HELPER FUNCTION
// ============================================================================

type CountryConfigInput = Omit<
  CountryConfig,
  | 'chatbot'
  | 'feedbackMessageType'
  | 'autoNewsletterSubscribe'
  | 'overrideCreditCardLogos'
  | 'priceDropProtection'
  | 'paymentModal'
  | 'isAsync'
  | 'measurementSystem'
  | 'calendar'
> & {
  chatbot?: boolean
  feedbackMessageType?: FeedbackMessageType
  autoNewsletterSubscribe?: boolean
  overrideCreditCardLogos?: string[] | null
  priceDropProtection?: boolean
  paymentModal?: boolean
  isAsync?: boolean
  measurementSystem?: MeasurementSystems
  calendar?: Partial<CountryConfig['calendar']>
}

function createCountryConfig(input: CountryConfigInput): CountryConfig {
  return {
    ...DEFAULT_COMMON_PROPS,
    ...input,
    calendar: {
      ...DEFAULT_CALENDAR,
      ...input.calendar,
    },
  }
}

// ============================================================================
// DATASOURCE
// ============================================================================

const countryDataSource: CountryDataSource = {
  gb: createCountryConfig({
    market: 'uk',
    countryCode: 'gb',
    countryName: 'United Kingdom',
    locale: 'en-GB',
    language: 'en',
    countryLanguage: 'English',
    currency: 'GBP',
    currencySymbol: '£',
    contactEmail: 'emily@cs.stayforlong.com',
    phone: '+441173702990',
    subdomain: null,
    topdomain: 'co.uk',
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
  }),

  fr: createCountryConfig({
    market: 'fr',
    countryCode: 'fr',
    countryName: 'France',
    locale: 'fr-FR',
    language: 'fr',
    countryLanguage: 'Français',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'gabrielle@cs.stayforlong.com',
    phone: '+33428770117',
    subdomain: null,
    topdomain: 'fr',
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.fr}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.fr}`,
  }),

  us: createCountryConfig({
    market: 'us',
    countryCode: 'us',
    countryName: 'United States',
    locale: 'en-US',
    language: 'en',
    countryLanguage: 'English',
    currency: 'USD',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: '+19493770263',
    measurementSystem: 'imperial',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    autoNewsletterSubscribe: true,
    calendar: { weekStartsOn: 0 },
  }),

  de: createCountryConfig({
    market: 'de',
    countryCode: 'de',
    countryName: 'Deutschland',
    locale: 'de-DE',
    language: 'de',
    countryLanguage: 'Deutsch',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'larissa@cs.stayforlong.com',
    phone: '+4906987001476',
    subdomain: null,
    topdomain: 'de',
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.de}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.de}`,
  }),

  it: createCountryConfig({
    market: 'it',
    countryCode: 'it',
    countryName: 'Italia',
    locale: 'it-IT',
    language: 'it',
    countryLanguage: 'Italiano',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'giulia@cs.stayforlong.com',
    phone: '+3908119020179',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.it}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.it}`,
  }),

  es: createCountryConfig({
    market: 'es',
    countryCode: 'es',
    countryName: 'España',
    locale: 'es-ES',
    language: 'es',
    countryLanguage: 'Español',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'claudia@cs.stayforlong.com',
    phone: '+34911795385',
    subdomain: 'es',
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.es}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.es}`,
    chatbot: true,
  }),

  nl: createCountryConfig({
    market: 'nl',
    countryCode: 'nl',
    countryName: 'Nederland',
    locale: 'nl-NL',
    language: 'nl',
    countryLanguage: 'Nederlands',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'sanne@cs.stayforlong.com',
    phone: '+31202625516',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.nl}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.nl}`,
  }),

  beFR: createCountryConfig({
    market: 'be',
    countryCode: 'be',
    countryName: 'Belgique',
    locale: 'fr-BE',
    language: 'fr',
    countryLanguage: 'Français',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'gabrielle@cs.stayforlong.com',
    phone: '+33428770117',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.fr}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.fr}`,
  }),

  beNL: createCountryConfig({
    market: 'be',
    countryCode: 'be',
    countryName: 'België',
    locale: 'nl-BE',
    language: 'nl',
    countryLanguage: 'Nederlands',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'gabrielle@cs.stayforlong.com',
    phone: '+33428770117',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.nl}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.nl}`,
  }),

  caEN: createCountryConfig({
    market: 'ca',
    countryCode: 'ca',
    countryName: 'Canada',
    locale: 'en-CA',
    language: 'en',
    countryLanguage: 'English',
    currency: 'CAD',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: '+17808517445',
    measurementSystem: 'imperial',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    calendar: { weekStartsOn: 0 },
  }),

  caFR: createCountryConfig({
    market: 'ca',
    countryCode: 'ca',
    countryName: 'Canada',
    locale: 'fr-CA',
    language: 'fr',
    countryLanguage: 'Français',
    currency: 'CAD',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: '+17808517445',
    measurementSystem: 'imperial',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.fr}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.fr}`,
    calendar: { weekStartsOn: 0 },
  }),

  dk: createCountryConfig({
    market: 'dk',
    countryCode: 'dk',
    countryName: 'Danmark',
    locale: 'da-DK',
    language: 'da',
    countryLanguage: 'Dansk',
    currency: 'DKK',
    currencySymbol: 'kr',
    contactEmail: 'lucy@cs.stayforlong.com',
    phone: '+4532426610',
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
  }),

  gr: createCountryConfig({
    market: 'gr',
    countryCode: 'gr',
    countryName: 'Ελλάδα',
    locale: 'el-GR',
    language: 'el',
    countryLanguage: 'Eλληνικά',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'anastasia@cs.stayforlong.com',
    phone: '+302111984343',
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
  }),

  ie: createCountryConfig({
    market: 'ie',
    countryCode: 'ie',
    countryName: 'Ireland',
    locale: 'en-IE',
    language: 'en',
    countryLanguage: 'English',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'lucy@cs.stayforlong.com',
    phone: '+35315460751',
    subdomain: 'ie',
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
  }),

  no: createCountryConfig({
    market: 'no',
    countryCode: 'no',
    countryName: 'Norge',
    locale: 'no-NN',
    language: 'nn',
    countryLanguage: 'Norsk',
    currency: 'NOK',
    currencySymbol: 'kr',
    contactEmail: 'lucy@cs.stayforlong.com',
    phone: '+4723965735',
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
  }),

  at: createCountryConfig({
    market: 'at',
    countryCode: 'at',
    countryName: 'Österreich',
    locale: 'de-AT',
    language: 'de',
    countryLanguage: 'Deutsch',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'johanna@cs.stayforlong.com',
    phone: '+43720105209',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.de}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.de}`,
  }),

  pl: createCountryConfig({
    market: 'pl',
    countryCode: 'pl',
    countryName: 'Polska',
    locale: 'pl-PL',
    language: 'pl',
    countryLanguage: 'Polski',
    currency: 'PLN',
    currencySymbol: 'zł',
    contactEmail: 'lucy@cs.stayforlong.com',
    phone: '+48158881008',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.pl}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.pl}`,
    chatbot: true,
  }),

  pt: createCountryConfig({
    market: 'pt',
    countryCode: 'pt',
    countryName: 'Portugal',
    locale: 'pt-PT',
    language: 'pt',
    countryLanguage: 'Português',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'isabel@cs.stayforlong.com',
    phone: '+351308800181',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.pt}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.pt}`,
    chatbot: true,
  }),

  ro: createCountryConfig({
    market: 'ro',
    countryCode: 'ro',
    countryName: 'România',
    locale: 'ro-RO',
    language: 'ro',
    countryLanguage: 'Română',
    currency: 'RON',
    currencySymbol: 'lei',
    contactEmail: 'maria@cs.stayforlong.com',
    phone: '+40316302322',
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    overrideCreditCardLogos: ['visa', 'mastercard'],
  }),

  chDE: createCountryConfig({
    market: 'ch',
    countryCode: 'ch',
    countryName: 'Schweiz',
    locale: 'de-CH',
    language: 'de',
    countryLanguage: 'Deutsch',
    currency: 'CHF',
    currencySymbol: '₣',
    contactEmail: 'emma@cs.stayforlong.com',
    phone: '+41435020028',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.de}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.de}`,
  }),

  chFR: createCountryConfig({
    market: 'ch',
    countryCode: 'ch',
    countryName: 'Suisse',
    locale: 'fr-CH',
    language: 'fr',
    countryLanguage: 'Français',
    currency: 'CHF',
    currencySymbol: '₣',
    contactEmail: 'emma@cs.stayforlong.com',
    phone: '+41435020028',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.fr}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.fr}`,
  }),

  chIT: createCountryConfig({
    market: 'ch',
    countryCode: 'ch',
    countryName: 'Svizzera',
    locale: 'it-CH',
    language: 'it',
    countryLanguage: 'Italiano',
    currency: 'CHF',
    currencySymbol: '₣',
    contactEmail: 'emma@cs.stayforlong.com',
    phone: '+41435020028',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.it}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.it}`,
  }),

  sk: createCountryConfig({
    market: 'sk',
    countryCode: 'sk',
    countryName: 'Slovensko',
    locale: 'sk-SK',
    language: 'sk',
    countryLanguage: 'Slovenský',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'viktoria@cs.stayforlong.com',
    phone: '+421232784592',
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.sk}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.sk}`,
    chatbot: true,
  }),

  za: createCountryConfig({
    market: 'za',
    countryCode: 'za',
    countryName: 'South Africa',
    locale: 'en-ZA',
    language: 'en',
    countryLanguage: 'English',
    currency: 'ZAR',
    currencySymbol: 'R',
    contactEmail: 'shona@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
  }),

  fi: createCountryConfig({
    market: 'fi',
    countryCode: 'fi',
    countryName: 'Suomi',
    locale: 'fi-FI',
    language: 'fi',
    countryLanguage: 'Suomen kieli',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'lucy@cs.stayforlong.com',
    phone: '+358931589665',
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
  }),

  se: createCountryConfig({
    market: 'se',
    countryCode: 'se',
    countryName: 'Sverige',
    locale: 'sv-SE',
    language: 'sv',
    countryLanguage: 'Svenska',
    currency: 'SEK',
    currencySymbol: 'kr',
    contactEmail: 'lucy@cs.stayforlong.com',
    phone: '+46846501532',
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
  }),

  tr: createCountryConfig({
    market: 'tr',
    countryCode: 'tr',
    countryName: 'Türkiye',
    locale: 'tr-TR',
    language: 'tr',
    countryLanguage: 'Türkçe',
    currency: 'TRY',
    currencySymbol: '₺',
    contactEmail: 'zeynep@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: HELPCENTER_URL,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    overrideCreditCardLogos: ['visa', 'mastercard'],
  }),

  au: createCountryConfig({
    market: 'au',
    countryCode: 'au',
    countryName: 'Australia',
    locale: 'en-AU',
    language: 'en',
    countryLanguage: 'English',
    currency: 'AUD',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    feedbackMessageType: 'email',
    overrideCreditCardLogos: ['visa', 'mastercard'],
  }),

  mx: createCountryConfig({
    market: 'mx',
    countryCode: 'mx',
    countryName: 'Mexico',
    locale: 'es-MX',
    language: 'es',
    countryLanguage: 'Español',
    currency: 'MXN',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.es}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.es}`,
    chatbot: true,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { inanitionDays: 1 },
  }),

  br: createCountryConfig({
    market: 'br',
    countryCode: 'br',
    countryName: 'Brasil',
    locale: 'pt-BR',
    language: 'pt',
    countryLanguage: 'Português',
    currency: 'BRL',
    currencySymbol: 'R$',
    contactEmail: 'isabel@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH['pt-br']}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH['pt-br']}`,
    chatbot: true,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { inanitionDays: 1 },
  }),

  ee: createCountryConfig({
    market: 'ee',
    countryCode: 'ee',
    countryName: 'Eesti',
    locale: 'et-EE',
    language: 'et',
    countryLanguage: 'Eesti keel',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    calendar: { inanitionDays: 1 },
  }),

  hr: createCountryConfig({
    market: 'hr',
    countryCode: 'hr',
    countryName: 'Hrvatska',
    locale: 'hr-HR',
    language: 'hr',
    countryLanguage: 'Hrvatski',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    calendar: { inanitionDays: 1 },
  }),

  lt: createCountryConfig({
    market: 'lt',
    countryCode: 'lt',
    countryName: 'Lietuva',
    locale: 'lt-LT',
    language: 'lt',
    countryLanguage: 'Lietuvių',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    calendar: { inanitionDays: 1 },
  }),

  lv: createCountryConfig({
    market: 'lv',
    countryCode: 'lv',
    countryName: 'Latvija',
    locale: 'lv-LV',
    language: 'lv',
    countryLanguage: 'Latviešu',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    calendar: { inanitionDays: 1 },
  }),

  sl: createCountryConfig({
    market: 'si',
    countryCode: 'si',
    countryName: 'Slovenija',
    locale: 'sl-SI',
    language: 'sl',
    countryLanguage: 'Slovenščina',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    calendar: { inanitionDays: 1 },
  }),

  nz: createCountryConfig({
    market: 'nz',
    countryCode: 'nz',
    countryName: 'New Zealand',
    locale: 'en-NZ',
    language: 'en',
    countryLanguage: 'English',
    currency: 'NZD',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { inanitionDays: 1 },
  }),

  hu: createCountryConfig({
    market: 'hu',
    countryCode: 'hu',
    countryName: 'Hungry',
    locale: 'hu-HU',
    language: 'hu',
    countryLanguage: 'Hungarian',
    currency: 'HUF',
    currencySymbol: 'Ft',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { inanitionDays: 1 },
  }),

  cz: createCountryConfig({
    market: 'cz',
    countryCode: 'cz',
    countryName: 'Czech Republic',
    locale: 'cs-CZ',
    language: 'cs',
    countryLanguage: 'Czech',
    currency: 'CZK',
    currencySymbol: 'Kč',
    contactEmail: 'eurosupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { inanitionDays: 1 },
  }),

  xgAR: createCountryConfig({
    market: 'xg',
    countryCode: 'xg',
    countryName: 'Gulf Region',
    locale: 'ar-SA-u-ca-gregory-nu-latn',
    language: 'ar',
    countryLanguage: 'Arabic',
    currency: 'SAR',
    currencySymbol: 'ر.س',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.ar}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.ar}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, monthFormat: 'long', inanitionDays: 1 },
  }),

  xgEN: createCountryConfig({
    market: 'xg',
    countryCode: 'xg',
    countryName: 'Gulf Region',
    locale: 'en-SA-u-ca-gregory-nu-latn',
    language: 'en',
    countryLanguage: 'English',
    currency: 'SAR',
    currencySymbol: 'ر.س',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, inanitionDays: 1 },
  }),

  aeAR: createCountryConfig({
    market: 'ae',
    countryCode: 'ae',
    countryName: 'United Arab Emirates',
    locale: 'ar-AE-u-ca-gregory-nu-latn',
    language: 'ar',
    countryLanguage: 'Arabic',
    currency: 'AED',
    currencySymbol: 'د.إ',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.ar}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.ar}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, monthFormat: 'long', inanitionDays: 1 },
  }),

  aeEN: createCountryConfig({
    market: 'ae',
    countryCode: 'ae',
    countryName: 'United Arab Emirates',
    locale: 'en-AE-u-ca-gregory-nu-latn',
    language: 'en',
    countryLanguage: 'English',
    currency: 'AED',
    currencySymbol: 'د.إ',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, inanitionDays: 1 },
  }),

  egAR: createCountryConfig({
    market: 'eg',
    countryCode: 'eg',
    countryName: 'Egypt',
    locale: 'ar-EG-u-ca-gregory-nu-latn',
    language: 'ar',
    countryLanguage: 'Arabic',
    currency: 'USD',
    currencySymbol: '$',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.ar}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.ar}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, monthFormat: 'long', inanitionDays: 1 },
  }),

  egEN: createCountryConfig({
    market: 'eg',
    countryCode: 'eg',
    countryName: 'Egypt',
    locale: 'en-EG-u-ca-gregory-nu-latn',
    language: 'en',
    countryLanguage: 'English',
    currency: 'USD',
    currencySymbol: '$',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, inanitionDays: 1 },
  }),

  sg: createCountryConfig({
    market: 'sg',
    countryCode: 'sg',
    countryName: 'Singapore',
    locale: 'en-SG',
    language: 'en',
    countryLanguage: 'English',
    currency: 'SGD',
    currencySymbol: '$',
    contactEmail: 'support@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    feedbackMessageType: 'email',
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 0, inanitionDays: 1 },
  }),

  ec: createCountryConfig({
    market: 'ec',
    countryCode: 'ec',
    countryName: 'Ecuador',
    locale: 'es-EC',
    language: 'es',
    countryLanguage: 'Español',
    currency: 'USD',
    currencySymbol: '$',
    contactEmail: 'claudia@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.es}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.es}`,
    chatbot: true,
    feedbackMessageType: 'email',
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { inanitionDays: 1 },
  }),

  dz: createCountryConfig({
    market: 'dz',
    countryCode: 'dz',
    countryName: 'Algérie',
    locale: 'fr-DZ',
    language: 'fr',
    countryLanguage: 'Français',
    currency: 'DZD',
    currencySymbol: '$',
    contactEmail: 'customersupport@cs.stayforlong.com',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.fr}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.fr}`,
    chatbot: true,
    feedbackMessageType: 'email',
    overrideCreditCardLogos: ['visa', 'mastercard'],
    calendar: { weekStartsOn: 6, inanitionDays: 7 },
  }),

  zz: createCountryConfig({
    market: 'ie',
    countryCode: 'zz',
    countryName: 'zz',
    locale: 'en-IE',
    language: 'en',
    countryLanguage: 'English',
    currency: 'EUR',
    currencySymbol: '€',
    contactEmail: '',
    phone: null,
    subdomain: 'ie',
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    feedbackMessageType: 'email',
    overrideCreditCardLogos: ['visa', 'mastercard'],
  }),

  co: createCountryConfig({
    market: 'co',
    countryCode: 'co',
    countryName: 'Colombia',
    locale: 'es-CO',
    language: 'es',
    countryLanguage: 'Español',
    currency: 'USD',
    currencySymbol: '$',
    contactEmail: '',
    phone: null,
    subdomain: null,
    helpCenterUrl: `${HELPCENTER_URL}${HELPCENTER_PATH.en}`,
    faqsUrl: `${HELPCENTER_URL}${FAQS_PATH.en}`,
    chatbot: true,
    feedbackMessageType: 'email',
    overrideCreditCardLogos: ['visa', 'mastercard'],
  }),
}

export const DEFAULT_COUNTRY_KEY: CountryConfigKey = 'us'

export const CUSTOM_DOMAINS = [
  ...new Set(
    Object.values(countryDataSource).flatMap(config => {
      const domains: string[] = []

      if (config.subdomain) {
        domains.push(`${config.subdomain}.stayforlong.com`)
      }

      if (config.topdomain) {
        domains.push(`stayforlong.${config.topdomain}`, `www.stayforlong.${config.topdomain}`)
      }

      return domains
    })
  ),
] as const

export { countryDataSource }
